import React, { useState, useEffect } from "react";
import {
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Grid,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Calculate as CalculateIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Functions as FunctionsIcon,
  CalendarMonth as CalendarMonthIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";

interface Division {
  name: string;
  courtsNeeded: number;
}

interface FinancialInputs {
  // League Structure
  numberOfPlayers: number;
  numberOfRounds: number;
  divisions: Division[];
  roundsIncludedInFee: number;
  
  // Courts
  courtCostReal: number;
  courtPriceCharged: number;
  
  // Balls
  sponsoredBallCans: number;
  ballCansPurchased: number;
  pricePerBallCan: number;
  
  // Payment
  playerFeeIncludingVAT: number;
  extraRoundPriceIncludingVAT: number;
  vatRate: number;
  averageExtraRoundsPerPlayer: number;
  
  // PayPal Fees
  paypalUserPercentage: number;
  paypalToPaypalFeeRate: number;
  nonPaypalFeeRate: number;
  nonPaypalFixedFee: number;
}

interface CalculatedResults {
  // Net values
  netPlayerFee: number;
  netExtraRoundPrice: number;
  
  // Courts
  courtsPerRound: number;
  totalCourtBookings: number;
  courtRevenue: number;
  courtCost: number;
  courtMargin: number;
  
  // Balls
  ballCost: number;
  totalBallCansAvailable: number;
  
  // Revenue
  baseRevenueNet: number;
  extraRoundsRevenueNet: number;
  totalRevenueNet: number;
  
  // PayPal Fees
  paypalFees: number;
  
  // Expenses & Profit
  totalExpenses: number;
  profit: number;
  profitPerPlayer: number;
  profitPerCourtBooking: number;
}

interface SchedulePlannerInputs {
  courtsAvailablePerSlot: number;
  slotsPerSaturday: number;
}

interface SlotAssignment {
  slotNumber: number;
  divisions: Division[];
  courtsUsed: number;
}

interface SchedulePlannerResults {
  courtsPerRound: number;
  slotsNeeded: number;
  saturdaysPerRound: number;
  slots: SlotAssignment[];
  warnings: string[];
}

function FinancialCalculator() {
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [currentInfoField, setCurrentInfoField] = useState<string>("");
  
  // Load initial state from localStorage or use defaults
  const getInitialInputs = (): FinancialInputs => {
    const saved = localStorage.getItem('financialCalculatorInputs');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved inputs:', e);
      }
    }
    return {
      // League Structure
      numberOfPlayers: 200,
      numberOfRounds: 12,
      divisions: [
        { name: "Master", courtsNeeded: 2 },
        { name: "Division 1", courtsNeeded: 4 },
        { name: "Division 2", courtsNeeded: 4 },
        { name: "Division 3", courtsNeeded: 3 },
      ],
      roundsIncludedInFee: 5,
      
      // Courts
      courtCostReal: 44,
      courtPriceCharged: 50,
      
      // Balls
      sponsoredBallCans: 100,
      ballCansPurchased: 200,
      pricePerBallCan: 3.5,
      
      // Payment
      playerFeeIncludingVAT: 100,
      extraRoundPriceIncludingVAT: 20,
      vatRate: 0.19,
      averageExtraRoundsPerPlayer: 2,
      
      // PayPal Fees
      paypalUserPercentage: 0.80, // 80% of users
      paypalToPaypalFeeRate: 0.009, // 0.90%
      nonPaypalFeeRate: 0.0299, // 2.99%
      nonPaypalFixedFee: 0.39, // €0.39
    };
  };

  const [inputs, setInputs] = useState<FinancialInputs>(getInitialInputs());

  const [results, setResults] = useState<CalculatedResults>({
    netPlayerFee: 0,
    netExtraRoundPrice: 0,
    courtsPerRound: 0,
    totalCourtBookings: 0,
    courtRevenue: 0,
    courtCost: 0,
    courtMargin: 0,
    ballCost: 0,
    totalBallCansAvailable: 0,
    baseRevenueNet: 0,
    extraRoundsRevenueNet: 0,
    totalRevenueNet: 0,
    paypalFees: 0,
    totalExpenses: 0,
    profit: 0,
    profitPerPlayer: 0,
    profitPerCourtBooking: 0,
  });

  const getInitialScheduleInputs = (): SchedulePlannerInputs => {
    const saved = localStorage.getItem('schedulePlannerInputs');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved schedule inputs:', e);
      }
    }
    return {
      courtsAvailablePerSlot: 4,
      slotsPerSaturday: 3,
    };
  };

  const [scheduleInputs, setScheduleInputs] = useState<SchedulePlannerInputs>(getInitialScheduleInputs());

  const [scheduleResults, setScheduleResults] = useState<SchedulePlannerResults>({
    courtsPerRound: 0,
    slotsNeeded: 0,
    saturdaysPerRound: 0,
    slots: [],
    warnings: [],
  });

  // Save inputs to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('financialCalculatorInputs', JSON.stringify(inputs));
  }, [inputs]);

  // Save schedule inputs to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('schedulePlannerInputs', JSON.stringify(scheduleInputs));
  }, [scheduleInputs]);

  // Calculate results whenever inputs change
  useEffect(() => {
    calculateResults();
  }, [inputs]);

  // Calculate schedule whenever schedule inputs or divisions change
  useEffect(() => {
    calculateSchedule();
  }, [scheduleInputs, inputs.divisions]);

  const calculateResults = () => {
    // 1. Net values
    const netPlayerFee = inputs.playerFeeIncludingVAT / (1 + inputs.vatRate);
    const netExtraRoundPrice = inputs.extraRoundPriceIncludingVAT / (1 + inputs.vatRate);

    // 2. Courts
    const courtsPerRound = inputs.divisions.reduce((sum, div) => sum + div.courtsNeeded, 0);
    const totalCourtBookings = courtsPerRound * inputs.numberOfRounds;
    const courtRevenue = totalCourtBookings * inputs.courtPriceCharged;
    const courtCost = totalCourtBookings * inputs.courtCostReal;
    const courtMargin = courtRevenue - courtCost;

    // 3. Balls
    const ballCost = inputs.ballCansPurchased * inputs.pricePerBallCan;
    const totalBallCansAvailable = inputs.sponsoredBallCans + inputs.ballCansPurchased;

    // 4. Base revenue
    const baseRevenueNet = inputs.numberOfPlayers * netPlayerFee;

    // 5. Extra rounds revenue
    const extraRoundsRevenueNet = 
      inputs.numberOfPlayers * 
      inputs.averageExtraRoundsPerPlayer * 
      netExtraRoundPrice;

    // 6. Total revenue
    const totalRevenueNet = baseRevenueNet + extraRoundsRevenueNet;

    // 7. PayPal Fees
    const playersWithPaypal = inputs.numberOfPlayers * inputs.paypalUserPercentage;
    const playersWithoutPaypal = inputs.numberOfPlayers - playersWithPaypal;
    
    // PayPal to PayPal fees (percentage only)
    const paypalToPaypalFees = playersWithPaypal * inputs.playerFeeIncludingVAT * inputs.paypalToPaypalFeeRate;
    
    // Non-PayPal fees (percentage + fixed)
    const nonPaypalFees = playersWithoutPaypal * (
      inputs.playerFeeIncludingVAT * inputs.nonPaypalFeeRate + inputs.nonPaypalFixedFee
    );
    
    const paypalFees = paypalToPaypalFees + nonPaypalFees;

    // 8. Total expenses
    const totalExpenses = courtCost + ballCost + paypalFees;

    // 9. Profit
    const profit = totalRevenueNet - totalExpenses;

    // 10. Helpful metrics
    const profitPerPlayer = profit / inputs.numberOfPlayers;
    const profitPerCourtBooking = totalCourtBookings > 0 ? profit / totalCourtBookings : 0;

    setResults({
      netPlayerFee,
      netExtraRoundPrice,
      courtsPerRound,
      totalCourtBookings,
      courtRevenue,
      courtCost,
      courtMargin,
      ballCost,
      totalBallCansAvailable,
      baseRevenueNet,
      extraRoundsRevenueNet,
      totalRevenueNet,
      paypalFees,
      totalExpenses,
      profit,
      profitPerPlayer,
      profitPerCourtBooking,
    });
  };

  const calculateSchedule = () => {
    const { courtsAvailablePerSlot, slotsPerSaturday } = scheduleInputs;
    const divisions = inputs.divisions;

    // Calculate total courts per round
    const courtsPerRound = divisions.reduce((sum, div) => sum + div.courtsNeeded, 0);

    // Check for warnings
    const warnings: string[] = [];
    divisions.forEach(div => {
      if (div.courtsNeeded > courtsAvailablePerSlot) {
        warnings.push(`⚠️ ${div.name} requires ${div.courtsNeeded} courts but only ${courtsAvailablePerSlot} courts are available per slot.`);
      }
    });

    // Greedy bin packing algorithm
    // Sort divisions by courts needed (descending)
    const sortedDivisions = [...divisions].sort((a, b) => b.courtsNeeded - a.courtsNeeded);

    const slots: SlotAssignment[] = [];

    sortedDivisions.forEach(division => {
      // Skip divisions that can't fit in any slot
      if (division.courtsNeeded > courtsAvailablePerSlot) {
        return;
      }

      // Try to find a slot where this division fits
      let placed = false;
      for (const slot of slots) {
        const remainingCapacity = courtsAvailablePerSlot - slot.courtsUsed;
        if (division.courtsNeeded <= remainingCapacity) {
          slot.divisions.push(division);
          slot.courtsUsed += division.courtsNeeded;
          placed = true;
          break;
        }
      }

      // If no slot found, create a new one
      if (!placed) {
        slots.push({
          slotNumber: slots.length + 1,
          divisions: [division],
          courtsUsed: division.courtsNeeded,
        });
      }
    });

    const slotsNeeded = slots.length;
    const saturdaysPerRound = Math.ceil(slotsNeeded / slotsPerSaturday);

    setScheduleResults({
      courtsPerRound,
      slotsNeeded,
      saturdaysPerRound,
      slots,
      warnings,
    });
  };

  const handleScheduleInputChange = (field: keyof SchedulePlannerInputs, value: number) => {
    setScheduleInputs(prev => ({ ...prev, [field]: value }));
  };

  const handleInputChange = (field: keyof FinancialInputs, value: number) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const handleDivisionChange = (index: number, field: keyof Division, value: string | number) => {
    const newDivisions = [...inputs.divisions];
    newDivisions[index] = { ...newDivisions[index], [field]: value };
    setInputs(prev => ({ ...prev, divisions: newDivisions }));
  };

  const addDivision = () => {
    setInputs(prev => ({
      ...prev,
      divisions: [...prev.divisions, { name: `Division ${prev.divisions.length + 1}`, courtsNeeded: 2 }]
    }));
  };

  const removeDivision = (index: number) => {
    if (inputs.divisions.length > 1) {
      const newDivisions = inputs.divisions.filter((_, i) => i !== index);
      setInputs(prev => ({ ...prev, divisions: newDivisions }));
    }
  };

  const calculateScenario = (players: number): number => {
    const netPlayerFee = inputs.playerFeeIncludingVAT / (1 + inputs.vatRate);
    const netExtraRoundPrice = inputs.extraRoundPriceIncludingVAT / (1 + inputs.vatRate);
    const courtsPerRound = inputs.divisions.reduce((sum, div) => sum + div.courtsNeeded, 0);
    const totalCourtBookings = courtsPerRound * inputs.numberOfRounds;
    const courtCost = totalCourtBookings * inputs.courtCostReal;
    const ballCost = inputs.ballCansPurchased * inputs.pricePerBallCan;
    
    // Calculate PayPal fees for this scenario
    const playersWithPaypal = players * inputs.paypalUserPercentage;
    const playersWithoutPaypal = players - playersWithPaypal;
    const paypalToPaypalFees = playersWithPaypal * inputs.playerFeeIncludingVAT * inputs.paypalToPaypalFeeRate;
    const nonPaypalFees = playersWithoutPaypal * (inputs.playerFeeIncludingVAT * inputs.nonPaypalFeeRate + inputs.nonPaypalFixedFee);
    const paypalFees = paypalToPaypalFees + nonPaypalFees;
    
    const baseRevenueNet = players * netPlayerFee;
    const extraRoundsRevenueNet = players * inputs.averageExtraRoundsPerPlayer * netExtraRoundPrice;
    const totalRevenueNet = baseRevenueNet + extraRoundsRevenueNet;
    const totalExpenses = courtCost + ballCost + paypalFees;
    
    return totalRevenueNet - totalExpenses;
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Reset all values to defaults? This will clear all your changes.')) {
      localStorage.removeItem('financialCalculatorInputs');
      localStorage.removeItem('schedulePlannerInputs');
      window.location.reload();
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number, decimals: number = 2) => {
    return value.toFixed(decimals);
  };

  // Field descriptions for info dialogs
  const fieldDescriptions: Record<string, { title: string; description: string; example?: string }> = {
    numberOfPlayers: {
      title: "Number of Players",
      description: "Total number of players registered for the league season. This affects revenue calculations directly.",
      example: "200 players"
    },
    numberOfRounds: {
      title: "Number of Rounds",
      description: "Total number of league rounds/league days in the season. Used to calculate total court bookings needed.",
      example: "12 rounds"
    },
    roundsIncludedInFee: {
      title: "Rounds Included in Fee",
      description: "Number of rounds covered by the base player fee. Players pay extra for additional rounds beyond this.",
      example: "5 rounds included"
    },
    courtCostReal: {
      title: "Court Cost (Real)",
      description: "The actual cost you pay to the court facility per court per round.",
      example: "€44 per court"
    },
    courtPriceCharged: {
      title: "Court Price (Charged)",
      description: "The price you charge the league (factored into player fees) per court per round. The difference is your court margin.",
      example: "€50 per court"
    },
    sponsoredBallCans: {
      title: "Sponsored Ball Cans",
      description: "Number of ball cans provided by sponsors at no cost. These don't affect your expenses.",
      example: "100 cans"
    },
    ballCansPurchased: {
      title: "Ball Cans Purchased",
      description: "Number of ball cans you purchase. Multiplied by price per can for total ball cost.",
      example: "200 cans"
    },
    pricePerBallCan: {
      title: "Price per Ball Can",
      description: "Cost per ball can that you purchase. Usually 3-4 balls per can.",
      example: "€3.50 per can"
    },
    playerFeeIncludingVAT: {
      title: "Player Fee (incl. VAT)",
      description: "The total fee charged to each player for the season, including VAT. System calculates net revenue by removing VAT.",
      example: "€100 per player"
    },
    extraRoundPriceIncludingVAT: {
      title: "Extra Round Price (incl. VAT)",
      description: "Price charged per extra round beyond the rounds included in base fee, including VAT.",
      example: "€20 per extra round"
    },
    vatRate: {
      title: "VAT Rate",
      description: "Value Added Tax rate as a decimal. Used to calculate net (VAT-excluded) revenue. Standard German VAT is 0.19 (19%).",
      example: "0.19 for 19%"
    },
    averageExtraRoundsPerPlayer: {
      title: "Average Extra Rounds per Player",
      description: "Expected average number of extra rounds players will participate in beyond included rounds. Used to estimate extra round revenue.",
      example: "2 extra rounds"
    },
    paypalUserPercentage: {
      title: "PayPal User Percentage",
      description: "Percentage of players who pay using PayPal (lower fees). Remaining players pay via card/bank (higher fees).",
      example: "0.80 means 80% use PayPal"
    },
    paypalToPaypalFeeRate: {
      title: "PayPal to PayPal Fee Rate",
      description: "Transaction fee rate when both sender and receiver have PayPal. Typically 0.90% (0.009).",
      example: "0.009 for 0.90%"
    },
    nonPaypalFeeRate: {
      title: "Non-PayPal Fee Rate",
      description: "Transaction fee rate when sender doesn't have PayPal. Typically 2.99% (0.0299).",
      example: "0.0299 for 2.99%"
    },
    nonPaypalFixedFee: {
      title: "Non-PayPal Fixed Fee",
      description: "Fixed transaction fee when sender doesn't have PayPal. Typically €0.39 per transaction.",
      example: "€0.39 per transaction"
    },
  };

  const handleOpenInfo = (field: string) => {
    setCurrentInfoField(field);
    setInfoDialogOpen(true);
  };

  const handleCloseInfo = () => {
    setInfoDialogOpen(false);
    setCurrentInfoField("");
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2, flexWrap: "wrap", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <CalculateIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: "primary.main" }} />
          <Box>
            <Typography variant="h4" sx={{ color: "primary.main", fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
              League Financial Calculator
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Calculate the economics of your padel league season
            </Typography>
          </Box>
        </Box>
        <Button 
          variant="outlined" 
          color="warning"
          size="small"
          onClick={handleResetToDefaults}
          sx={{ minWidth: 'fit-content' }}
        >
          Reset to Defaults
        </Button>
      </Box>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* LEFT SIDE: INPUTS */}
        <Grid item xs={12} lg={6}>
          {/* League Structure */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                📊 League Structure
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Number of Players"
                    type="number"
                    value={inputs.numberOfPlayers}
                    onChange={(e) => handleInputChange('numberOfPlayers', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('numberOfPlayers')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Number of Rounds"
                    type="number"
                    value={inputs.numberOfRounds}
                    onChange={(e) => handleInputChange('numberOfRounds', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('numberOfRounds')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Rounds Included in Fee"
                    type="number"
                    value={inputs.roundsIncludedInFee}
                    onChange={(e) => handleInputChange('roundsIncludedInFee', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('roundsIncludedInFee')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="subtitle2">Divisions</Typography>
                  <Button 
                    size="small" 
                    startIcon={<AddIcon />}
                    onClick={addDivision}
                  >
                    Add Division
                  </Button>
                </Box>
                
                {inputs.divisions.map((division, index) => (
                  <Box key={index} sx={{ display: "flex", gap: 1, mb: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                    <TextField
                      fullWidth
                      label="Division Name"
                      value={division.name}
                      onChange={(e) => handleDivisionChange(index, 'name', e.target.value)}
                      size="small"
                      sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
                    />
                    <TextField
                      label="Courts"
                      type="number"
                      value={division.courtsNeeded}
                      onChange={(e) => handleDivisionChange(index, 'courtsNeeded', Number(e.target.value))}
                      InputProps={{ inputProps: { min: 0 } }}
                      size="small"
                      sx={{ width: { xs: '120px', sm: '100px' } }}
                    />
                    <IconButton 
                      color="error" 
                      onClick={() => removeDivision(index)}
                      disabled={inputs.divisions.length === 1}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Courts */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                🎾 Courts
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Court Cost (Real) €"
                    type="number"
                    value={inputs.courtCostReal}
                    onChange={(e) => handleInputChange('courtCostReal', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0, step: 0.5 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('courtCostReal')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Court Price (Charged) €"
                    type="number"
                    value={inputs.courtPriceCharged}
                    onChange={(e) => handleInputChange('courtPriceCharged', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0, step: 0.5 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('courtPriceCharged')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Balls */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                🎾 Balls
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Sponsored Ball Cans"
                    type="number"
                    value={inputs.sponsoredBallCans}
                    onChange={(e) => handleInputChange('sponsoredBallCans', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('sponsoredBallCans')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Ball Cans Purchased"
                    type="number"
                    value={inputs.ballCansPurchased}
                    onChange={(e) => handleInputChange('ballCansPurchased', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('ballCansPurchased')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Price per Ball Can €"
                    type="number"
                    value={inputs.pricePerBallCan}
                    onChange={(e) => handleInputChange('pricePerBallCan', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0, step: 0.1 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('pricePerBallCan')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Payment */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                💰 Payment
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Player Fee (incl. VAT) €"
                    type="number"
                    value={inputs.playerFeeIncludingVAT}
                    onChange={(e) => handleInputChange('playerFeeIncludingVAT', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0, step: 5 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('playerFeeIncludingVAT')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Extra Round Price (incl. VAT) €"
                    type="number"
                    value={inputs.extraRoundPriceIncludingVAT}
                    onChange={(e) => handleInputChange('extraRoundPriceIncludingVAT', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0, step: 5 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('extraRoundPriceIncludingVAT')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="VAT Rate"
                    type="number"
                    value={inputs.vatRate}
                    onChange={(e) => handleInputChange('vatRate', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0, max: 1, step: 0.01 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('vatRate')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                    helperText="Example: 0.19 for 19%"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Avg Extra Rounds per Player"
                    type="number"
                    value={inputs.averageExtraRoundsPerPlayer}
                    onChange={(e) => handleInputChange('averageExtraRoundsPerPlayer', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0, step: 0.5 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('averageExtraRoundsPerPlayer')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 2 }}>
                💳 PayPal Transaction Fees
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="PayPal User Percentage"
                    type="number"
                    value={inputs.paypalUserPercentage}
                    onChange={(e) => handleInputChange('paypalUserPercentage', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0, max: 1, step: 0.01 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('paypalUserPercentage')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                    helperText="Example: 0.80 for 80%"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="PayPal to PayPal Fee Rate"
                    type="number"
                    value={inputs.paypalToPaypalFeeRate}
                    onChange={(e) => handleInputChange('paypalToPaypalFeeRate', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0, max: 1, step: 0.0001 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('paypalToPaypalFeeRate')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                    helperText="Example: 0.009 for 0.90%"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Non-PayPal Fee Rate"
                    type="number"
                    value={inputs.nonPaypalFeeRate}
                    onChange={(e) => handleInputChange('nonPaypalFeeRate', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0, max: 1, step: 0.0001 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('nonPaypalFeeRate')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                    helperText="Example: 0.0299 for 2.99%"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Non-PayPal Fixed Fee €"
                    type="number"
                    value={inputs.nonPaypalFixedFee}
                    onChange={(e) => handleInputChange('nonPaypalFixedFee', Number(e.target.value))}
                    InputProps={{ 
                      inputProps: { min: 0, step: 0.01 },
                      endAdornment: (
                        <Tooltip title="Click for info">
                          <IconButton size="small" onClick={() => handleOpenInfo('nonPaypalFixedFee')}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                    helperText="Fixed fee per transaction"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT SIDE: RESULTS */}
        <Grid item xs={12} lg={6}>
          {/* Season Summary */}
          <Card sx={{ mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                📈 Season Summary
              </Typography>
              <Divider sx={{ mb: 2, borderColor: 'success.dark' }} />
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                    <Typography variant="caption" color="text.secondary">Total Revenue (Net)</Typography>
                    <Typography variant="h6" color="primary">{formatCurrency(results.totalRevenueNet)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                    <Typography variant="caption" color="text.secondary">Total Expenses</Typography>
                    <Typography variant="h6" color="error">{formatCurrency(results.totalExpenses)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: results.profit >= 0 ? 'success.main' : 'error.main', color: 'white' }}>
                    <Typography variant="caption">PROFIT</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{formatCurrency(results.profit)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                    <Typography variant="caption" color="text.secondary">Profit per Player</Typography>
                    <Typography variant="h6">{formatCurrency(results.profitPerPlayer)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                    <Typography variant="caption" color="text.secondary">Profit per Court</Typography>
                    <Typography variant="h6">{formatCurrency(results.profitPerCourtBooking)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                    <Typography variant="caption" color="text.secondary">Total Court Bookings</Typography>
                    <Typography variant="h6">{results.totalCourtBookings}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                    <Typography variant="caption" color="text.secondary">Total Balls Available</Typography>
                    <Typography variant="h6">{results.totalBallCansAvailable} cans</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Calculation Breakdown */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FunctionsIcon />
                Calculation Breakdown
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">1. Net Values (Exclude VAT)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography 
                    variant="body2" 
                    component="div" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      whiteSpace: 'pre-line',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      overflowX: 'auto'
                    }}
                  >
                    {`Player Fee Net = ${inputs.playerFeeIncludingVAT} / (1 + ${inputs.vatRate})
= ${formatCurrency(inputs.playerFeeIncludingVAT / (1 + inputs.vatRate))}

Extra Round Price Net = ${inputs.extraRoundPriceIncludingVAT} / (1 + ${inputs.vatRate})
= ${formatCurrency(inputs.extraRoundPriceIncludingVAT / (1 + inputs.vatRate))}`}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">2. Court Bookings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography 
                    variant="body2" 
                    component="div" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      whiteSpace: 'pre-line',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      overflowX: 'auto'
                    }}
                  >
                    {`Total Courts per Round = ${inputs.divisions.map(d => d.courtsNeeded).reduce((a, b) => a + b, 0)}
Total Rounds = ${inputs.numberOfRounds}

Total Court Bookings = ${inputs.divisions.map(d => d.courtsNeeded).reduce((a, b) => a + b, 0)} × ${inputs.numberOfRounds}
= ${results.totalCourtBookings} bookings`}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">3. Ball Inventory</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography 
                    variant="body2" 
                    component="div" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      whiteSpace: 'pre-line',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      overflowX: 'auto'
                    }}
                  >
                    {`Sponsored Cans = ${inputs.sponsoredBallCans}
Purchased Cans = ${inputs.ballCansPurchased}

Total Balls = ${inputs.sponsoredBallCans} + ${inputs.ballCansPurchased}
= ${results.totalBallCansAvailable} cans`}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">4. Revenue Calculation</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography 
                    variant="body2" 
                    component="div" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      whiteSpace: 'pre-line',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      overflowX: 'auto'
                    }}
                  >
                    {`Base Revenue = ${inputs.numberOfPlayers} × ${formatCurrency(inputs.playerFeeIncludingVAT / (1 + inputs.vatRate))}
= ${formatCurrency(results.baseRevenueNet)}

Extra Rounds Revenue = ${inputs.numberOfPlayers} × ${inputs.averageExtraRoundsPerPlayer} × ${formatCurrency(inputs.extraRoundPriceIncludingVAT / (1 + inputs.vatRate))}
= ${formatCurrency(results.extraRoundsRevenueNet)}

Total Revenue (Net) = ${formatCurrency(results.baseRevenueNet)} + ${formatCurrency(results.extraRoundsRevenueNet)}
= ${formatCurrency(results.totalRevenueNet)}`}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">5. Expenses Calculation</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography 
                    variant="body2" 
                    component="div" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      whiteSpace: 'pre-line',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      overflowX: 'auto'
                    }}
                  >
                    {`Court Cost = ${results.totalCourtBookings} × ${formatCurrency(inputs.courtCostReal)}
= ${formatCurrency(results.courtCost)}

Ball Cost = ${inputs.ballCansPurchased} × ${formatCurrency(inputs.pricePerBallCan)}
= ${formatCurrency(results.ballCost)}

PayPal Fees = ${formatCurrency(results.paypalFees)}
(See PayPal Fees section for breakdown)

Total Expenses = ${formatCurrency(results.courtCost)} + ${formatCurrency(results.ballCost)} + ${formatCurrency(results.paypalFees)}
= ${formatCurrency(results.totalExpenses)}`}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">5.5. PayPal Fee Breakdown</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography 
                    variant="body2" 
                    component="div" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      whiteSpace: 'pre-line',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      overflowX: 'auto'
                    }}
                  >
                    {`Players with PayPal = ${inputs.numberOfPlayers} × ${(inputs.paypalUserPercentage * 100).toFixed(0)}%
= ${(inputs.numberOfPlayers * inputs.paypalUserPercentage).toFixed(1)} players

Players without PayPal = ${inputs.numberOfPlayers} - ${(inputs.numberOfPlayers * inputs.paypalUserPercentage).toFixed(1)}
= ${(inputs.numberOfPlayers * (1 - inputs.paypalUserPercentage)).toFixed(1)} players

PayPal-to-PayPal Fees:
${(inputs.numberOfPlayers * inputs.paypalUserPercentage).toFixed(1)} × ${formatCurrency(inputs.playerFeeIncludingVAT)} × ${(inputs.paypalToPaypalFeeRate * 100).toFixed(2)}%
= ${formatCurrency(inputs.numberOfPlayers * inputs.paypalUserPercentage * inputs.playerFeeIncludingVAT * inputs.paypalToPaypalFeeRate)}

Non-PayPal Fees (per player):
${formatCurrency(inputs.playerFeeIncludingVAT)} × ${(inputs.nonPaypalFeeRate * 100).toFixed(2)}% + ${formatCurrency(inputs.nonPaypalFixedFee)}
= ${formatCurrency(inputs.playerFeeIncludingVAT * inputs.nonPaypalFeeRate + inputs.nonPaypalFixedFee)}

Non-PayPal Fees (total):
${(inputs.numberOfPlayers * (1 - inputs.paypalUserPercentage)).toFixed(1)} × ${formatCurrency(inputs.playerFeeIncludingVAT * inputs.nonPaypalFeeRate + inputs.nonPaypalFixedFee)}
= ${formatCurrency(inputs.numberOfPlayers * (1 - inputs.paypalUserPercentage) * (inputs.playerFeeIncludingVAT * inputs.nonPaypalFeeRate + inputs.nonPaypalFixedFee))}

Total PayPal Fees = ${formatCurrency(results.paypalFees)}`}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">6. Profit & Margins</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography 
                    variant="body2" 
                    component="div" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      whiteSpace: 'pre-line',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      overflowX: 'auto'
                    }}
                  >
                    {`Net Profit = ${formatCurrency(results.totalRevenueNet)} - ${formatCurrency(results.totalExpenses)}
= ${formatCurrency(results.profit)}

Profit Margin = (${formatCurrency(results.profit)} / ${formatCurrency(results.totalRevenueNet)}) × 100
= ${((results.profit / results.totalRevenueNet) * 100).toFixed(2)}%

Profit per Player = ${formatCurrency(results.profit)} / ${inputs.numberOfPlayers}
= ${formatCurrency(results.profitPerPlayer)}

Profit per Court = ${formatCurrency(results.profit)} / ${results.totalCourtBookings}
= ${formatCurrency(results.profitPerCourtBooking)}

Court Margin = ${formatCurrency(results.courtRevenue)} - ${formatCurrency(results.courtCost)}
= ${formatCurrency(results.courtMargin)}`}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>

          {/* Detailed Breakdown */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 Detailed Breakdown
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                        Revenue
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Base Revenue (Net)</TableCell>
                      <TableCell align="right">{formatCurrency(results.baseRevenueNet)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Extra Rounds Revenue (Net)</TableCell>
                      <TableCell align="right">{formatCurrency(results.extraRoundsRevenueNet)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ fontWeight: 'bold', bgcolor: 'primary.light' }}>
                      <TableCell>Total Revenue (Net)</TableCell>
                      <TableCell align="right">{formatCurrency(results.totalRevenueNet)}</TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold', bgcolor: 'action.hover', pt: 2 }}>
                        Expenses
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Court Cost</TableCell>
                      <TableCell align="right">{formatCurrency(results.courtCost)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Ball Cost</TableCell>
                      <TableCell align="right">{formatCurrency(results.ballCost)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>PayPal Fees</TableCell>
                      <TableCell align="right">{formatCurrency(results.paypalFees)}</TableCell>
                    </TableRow>
                    <TableRow sx={{ fontWeight: 'bold', bgcolor: 'error.light' }}>
                      <TableCell>Total Expenses</TableCell>
                      <TableCell align="right">{formatCurrency(results.totalExpenses)}</TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold', bgcolor: 'action.hover', pt: 2 }}>
                        Court Economics
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Courts per Round</TableCell>
                      <TableCell align="right">{results.courtsPerRound}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Court Revenue</TableCell>
                      <TableCell align="right">{formatCurrency(results.courtRevenue)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Court Margin</TableCell>
                      <TableCell align="right" sx={{ color: results.courtMargin >= 0 ? 'success.main' : 'error.main' }}>
                        {formatCurrency(results.courtMargin)}
                      </TableCell>
                    </TableRow>
                    
                    <TableRow>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold', bgcolor: 'action.hover', pt: 2 }}>
                        Net Calculations
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Net Player Fee (excl. VAT)</TableCell>
                      <TableCell align="right">{formatCurrency(results.netPlayerFee)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Net Extra Round Price (excl. VAT)</TableCell>
                      <TableCell align="right">{formatCurrency(results.netExtraRoundPrice)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Scenario Comparison */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                🔮 Scenario Comparison
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Quick profit comparison for different player counts (keeping all other variables the same)
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>150 Players</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {formatCurrency(calculateScenario(150))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Per Player: {formatCurrency(calculateScenario(150) / 150)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.main', color: 'white' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>200 Players</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {formatCurrency(calculateScenario(200))}
                    </Typography>
                    <Typography variant="caption">
                      Per Player: {formatCurrency(calculateScenario(200) / 200)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.dark', color: 'white' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>250 Players</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {formatCurrency(calculateScenario(250))}
                    </Typography>
                    <Typography variant="caption">
                      Per Player: {formatCurrency(calculateScenario(250) / 250)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> These scenarios use the current court, ball, and PayPal fee costs. 
                  Adjusting costs for higher player counts may yield different results.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SCHEDULE PLANNER SECTION */}
      <Divider sx={{ my: { xs: 4, md: 6 } }}>
        <Typography 
          variant="h5" 
          sx={{ 
            display: "flex", 
            alignItems: "center", 
            gap: 1, 
            fontWeight: 'bold',
            fontSize: { xs: '1.25rem', sm: '1.5rem' }
          }}
        >
          <ScheduleIcon fontSize="large" color="primary" />
          League Schedule Planner
        </Typography>
      </Divider>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* LEFT SIDE: SCHEDULE INPUTS */}
        <Grid item xs={12} lg={6}>
          {/* Schedule Configuration */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                ⚙️ Schedule Configuration
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Courts Available per Slot"
                    type="number"
                    value={scheduleInputs.courtsAvailablePerSlot}
                    onChange={(e) => handleScheduleInputChange('courtsAvailablePerSlot', Number(e.target.value))}
                    InputProps={{ inputProps: { min: 1 } }}
                    helperText="How many courts can be used simultaneously"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Slots per Saturday"
                    type="number"
                    value={scheduleInputs.slotsPerSaturday}
                    onChange={(e) => handleScheduleInputChange('slotsPerSaturday', Number(e.target.value))}
                    InputProps={{ inputProps: { min: 1 } }}
                    helperText="How many time slots per Saturday"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Divisions Reference */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                📋 Divisions (from Financial Calculator)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Division</strong></TableCell>
                      <TableCell align="right"><strong>Courts Needed</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inputs.divisions.map((div, index) => (
                      <TableRow key={index}>
                        <TableCell>{div.name}</TableCell>
                        <TableCell align="right">{div.courtsNeeded}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Edit divisions in the Financial Calculator section above
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT SIDE: SCHEDULE RESULTS */}
        <Grid item xs={12} lg={6}>
          {/* Warnings */}
          {scheduleResults.warnings.length > 0 && (
            <Card sx={{ mb: 3, bgcolor: 'error.light' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <WarningIcon />
                  Scheduling Warnings
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {scheduleResults.warnings.map((warning, index) => (
                  <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                    {warning}
                  </Typography>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Round Schedule Simulation */}
          <Card sx={{ mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                📊 Round Schedule Simulation
              </Typography>
              <Divider sx={{ mb: 2, borderColor: 'info.dark' }} />
              
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                    <Typography variant="caption" color="text.secondary">Courts per Round</Typography>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {scheduleResults.courtsPerRound}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                    <Typography variant="caption" color="text.secondary">Slots Needed</Typography>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {scheduleResults.slotsNeeded}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'background.paper' }}>
                    <Typography variant="caption" color="text.secondary">Saturdays per Round</Typography>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {scheduleResults.saturdaysPerRound}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Slot Breakdown */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🎯 Slot Breakdown
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {scheduleResults.slots.map((slot) => (
                <Paper key={slot.slotNumber} sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Slot {slot.slotNumber} ({slot.courtsUsed}/{scheduleInputs.courtsAvailablePerSlot} courts)
                  </Typography>
                  {slot.divisions.map((div, index) => (
                    <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                      • {div.name} ({div.courtsNeeded} courts)
                    </Typography>
                  ))}
                </Paper>
              ))}
            </CardContent>
          </Card>

          {/* Saturday Visualization */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarMonthIcon />
                Saturday Visualization
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {Array.from({ length: scheduleResults.saturdaysPerRound }, (_, satIndex) => {
                const startSlot = satIndex * scheduleInputs.slotsPerSaturday;
                const endSlot = Math.min(startSlot + scheduleInputs.slotsPerSaturday, scheduleResults.slotsNeeded);
                const saturdaySlots = scheduleResults.slots.slice(startSlot, endSlot);

                return (
                  <Paper key={satIndex} sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      Saturday {satIndex + 1}
                    </Typography>
                    {saturdaySlots.map((slot) => (
                      <Box key={slot.slotNumber} sx={{ mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 'bold' }}>
                          Slot {slot.slotNumber}
                        </Typography>
                        {slot.divisions.map((div, index) => (
                          <Typography key={index} variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                            {div.name} ({div.courtsNeeded} courts)
                          </Typography>
                        ))}
                      </Box>
                    ))}
                  </Paper>
                );
              })}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Info Dialog */}
      <Dialog open={infoDialogOpen} onClose={handleCloseInfo} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          {fieldDescriptions[currentInfoField]?.title || "Field Information"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {fieldDescriptions[currentInfoField]?.description}
          </Typography>
          {fieldDescriptions[currentInfoField]?.example && (
            <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Example:</strong> {fieldDescriptions[currentInfoField].example}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInfo} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FinancialCalculator;
